<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  show: { type: Boolean, default: false },
  prefilledCustomerId: { type: [Number, String], default: null },
  prefilledCustomerName: { type: String, default: '' },
});

const emit = defineEmits(['close', 'success']);

const store = useStore();
const getters = useStoreGetters();

const isSubmitting = ref(false);
const isLoadingChannels = ref(false);
const salesChannels = ref([]);

// Form State
const salesChannelId = ref('');
const custIndividualId = ref('');
const customerSearchName = ref('');
const notaOfficer = ref('CS Omnichannel');
const externalOrderId = ref('');
const notaPpn = ref('0');
const notaKeterangan = ref('');

// Dynamic Items List
const items = ref([
  {
    stock_id: '',
    spk_jumlah: 1,
    spk_ukuran: '',
    spk_warna: '',
    spk_finishing: '',
    disc_rate: 0,
    spk_keterangan: '',
  },
]);

const cetakersIntegration = computed(() => {
  const apps = getters['integrations/getAppIntegrations'].value;
  return apps?.find(app => app.id === 'cetakers_flow');
});

const apiEndpoint = computed(() => {
  const ep = cetakersIntegration.value?.hooks?.[0]?.settings?.api_endpoint || 'https://app.cetakers.com/fp-cetakers/api/v1/';
  return ep.endsWith('/') ? ep : `${ep}/`;
});

const apiKey = computed(() => cetakersIntegration.value?.hooks?.[0]?.settings?.api_key || '');

const fetchSalesChannels = async () => {
  if (!apiKey.value) return;
  isLoadingChannels.value = true;
  try {
    const res = await fetch(`${apiEndpoint.value}channels?status=1`, {
      headers: { Authorization: `Bearer ${apiKey.value}` },
    });
    if (res.ok) {
      const json = await res.json();
      salesChannels.value = json.data || json.results || [];
      if (salesChannels.value.length > 0 && !salesChannelId.value) {
        salesChannelId.value = salesChannels.value[0].channel_id;
      }
    }
  } catch (e) {
    console.error('Failed to fetch sales channels:', e);
  } finally {
    isLoadingChannels.value = false;
  }
};

const resetForm = () => {
  salesChannelId.value = salesChannels.value.length > 0 ? salesChannels.value[0].channel_id : '';
  custIndividualId.value = props.prefilledCustomerId || '';
  customerSearchName.value = props.prefilledCustomerName || '';
  notaOfficer.value = 'CS Omnichannel';
  externalOrderId.value = '';
  notaPpn.value = '0';
  notaKeterangan.value = '';
  items.value = [
    {
      stock_id: '',
      spk_jumlah: 1,
      spk_ukuran: '',
      spk_warna: '',
      spk_finishing: '',
      disc_rate: 0,
      spk_keterangan: '',
    },
  ];
};

watch(() => props.show, (newVal) => {
  if (newVal) {
    fetchSalesChannels();
    resetForm();
  }
});

onMounted(() => {
  if (props.show) {
    fetchSalesChannels();
    resetForm();
  }
});

const addItem = () => {
  items.value.push({
    stock_id: '',
    spk_jumlah: 1,
    spk_ukuran: '',
    spk_warna: '',
    spk_finishing: '',
    disc_rate: 0,
    spk_keterangan: '',
  });
};

const removeItem = (index) => {
  if (items.value.length > 1) {
    items.value.splice(index, 1);
  }
};

const handleSubmit = async () => {
  if (!apiKey.value) {
    useAlert('API Key Cetakers Flow belum dikonfigurasi.');
    return;
  }

  if (!custIndividualId.value) {
    useAlert('ID Pelanggan (cust_individual_id) wajib diisi.');
    return;
  }

  // Validate items
  const validItems = items.value.filter(it => it.stock_id && Number(it.spk_jumlah) > 0);
  if (validItems.length === 0) {
    useAlert('Minimal harus ada 1 item SPK dengan Stock ID dan Jumlah yang valid.');
    return;
  }

  const payload = {
    sales_channel_id: Number(salesChannelId.value) || 1,
    cust_individual_id: Number(custIndividualId.value),
    nota_officer: notaOfficer.value || 'CS Omnichannel',
    external_order_id: externalOrderId.value || undefined,
    nota_ppn: String(notaPpn.value || '0'),
    nota_keterangan: notaKeterangan.value || undefined,
    items: validItems.map(it => ({
      stock_id: Number(it.stock_id),
      spk_jumlah: Number(it.spk_jumlah),
      spk_ukuran: it.spk_ukuran || undefined,
      spk_warna: it.spk_warna || undefined,
      spk_finishing: it.spk_finishing || undefined,
      disc_rate: Number(it.disc_rate) || 0,
      spk_keterangan: it.spk_keterangan || undefined,
    })),
  };

  isSubmitting.value = true;

  try {
    const response = await fetch(`${apiEndpoint.value}order`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${apiKey.value}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const resData = await response.json();

    if (response.ok && (resData.status === 'success' || resData.data)) {
      const notaNo = resData.data?.nota_no || resData.data?.id || 'Nota';
      useAlert(`Pesanan Baru ${notaNo} berhasil dibuat di Cetakers Flow ERP!`);
      emit('success', resData.data);
      emit('close');
    } else {
      const errMsg = resData.message || resData.error || 'Gagal membuat pesanan di ERP.';
      useAlert(`Gagal: ${errMsg}`);
    }
  } catch (error) {
    console.error('Error creating order in Cetakers Flow:', error);
    useAlert(`Gagal terhubung ke API Cetakers Flow: ${error.message}`);
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto" @click.self="emit('close')">
    <div class="bg-white dark:bg-n-solid-1 rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden border border-n-weak my-8 flex flex-col max-h-[90vh]">
      
      <!-- Modal Header -->
      <div class="flex items-center justify-between px-6 py-4 border-b border-n-weak bg-n-surface-1 shrink-0">
        <div class="flex items-center gap-3">
          <div class="size-10 rounded-xl bg-n-brand-1 text-n-brand-9 flex items-center justify-center border border-n-brand-3">
            <Icon icon="i-lucide-shopping-bag" class="size-5" />
          </div>
          <div class="flex flex-col">
            <h3 class="font-bold text-base text-n-slate-12 m-0">Buat Pesanan Baru / Multi-Item SPK</h3>
            <p class="text-xs text-n-slate-10 m-0">Kirim nota pesanan langsung ke Cetakers Flow ERP</p>
          </div>
        </div>
        <button @click="emit('close')" class="p-1.5 text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 rounded-lg transition-colors">
          <Icon icon="i-lucide-x" class="size-5" />
        </button>
      </div>

      <!-- Modal Body (Form) -->
      <div class="p-6 overflow-y-auto flex-1 space-y-5">
        
        <!-- Header Info Grid -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 bg-n-surface-2 p-4 rounded-xl border border-n-weak">
          <!-- Sales Channel -->
          <div class="flex flex-col gap-1">
            <label class="text-xs font-semibold text-n-slate-11">Sales Channel (Saluran Penjualan)</label>
            <select
              v-model="salesChannelId"
              class="w-full px-3 py-2 text-xs bg-white dark:bg-n-solid-2 border border-n-weak rounded-lg focus:outline-none focus:ring-2 focus:ring-n-brand-6 text-n-slate-12 font-medium"
            >
              <option v-if="isLoadingChannels" value="">Memuat saluran sales...</option>
              <option v-for="ch in salesChannels" :key="ch.channel_id" :value="ch.channel_id">
                {{ ch.channel_name }} ({{ ch.channel_type || 'offline' }})
              </option>
              <option v-if="!isLoadingChannels && salesChannels.length === 0" value="1">WhatsApp Offline (Default #1)</option>
            </select>
          </div>

          <!-- Customer ID -->
          <div class="flex flex-col gap-1">
            <label class="text-xs font-semibold text-n-slate-11">ID Pelanggan (cust_individual_id) *</label>
            <input
              v-model="custIndividualId"
              type="number"
              placeholder="Contoh: 42"
              class="w-full px-3 py-2 text-xs bg-white dark:bg-n-solid-2 border border-n-weak rounded-lg focus:outline-none focus:ring-2 focus:ring-n-brand-6 text-n-slate-12 font-mono"
            />
            <span v-if="customerSearchName" class="text-[11px] text-n-brand-11">
              Pelanggan: <strong>{{ customerSearchName }}</strong>
            </span>
          </div>

          <!-- Officer / CS -->
          <div class="flex flex-col gap-1">
            <label class="text-xs font-semibold text-n-slate-11">Dibuat Oleh (Nota Officer)</label>
            <input
              v-model="notaOfficer"
              type="text"
              placeholder="Nama CS"
              class="w-full px-3 py-2 text-xs bg-white dark:bg-n-solid-2 border border-n-weak rounded-lg focus:outline-none focus:ring-2 focus:ring-n-brand-6 text-n-slate-12"
            />
          </div>

          <!-- External Order ID -->
          <div class="flex flex-col gap-1">
            <label class="text-xs font-semibold text-n-slate-11">External Order ID (Opsional)</label>
            <input
              v-model="externalOrderId"
              type="text"
              placeholder="Contoh: TKP-12345678"
              class="w-full px-3 py-2 text-xs bg-white dark:bg-n-solid-2 border border-n-weak rounded-lg focus:outline-none focus:ring-2 focus:ring-n-brand-6 text-n-slate-12 font-mono"
            />
          </div>

          <!-- PPN -->
          <div class="flex flex-col gap-1">
            <label class="text-xs font-semibold text-n-slate-11">PPN (%)</label>
            <input
              v-model="notaPpn"
              type="text"
              placeholder="0"
              class="w-full px-3 py-2 text-xs bg-white dark:bg-n-solid-2 border border-n-weak rounded-lg focus:outline-none focus:ring-2 focus:ring-n-brand-6 text-n-slate-12"
            />
          </div>

          <!-- Keterangan Nota -->
          <div class="flex flex-col gap-1">
            <label class="text-xs font-semibold text-n-slate-11">Catatan Nota (Opsional)</label>
            <input
              v-model="notaKeterangan"
              type="text"
              placeholder="Catatan pesanan..."
              class="w-full px-3 py-2 text-xs bg-white dark:bg-n-solid-2 border border-n-weak rounded-lg focus:outline-none focus:ring-2 focus:ring-n-brand-6 text-n-slate-12"
            />
          </div>
        </div>

        <!-- Items SPK Section -->
        <div class="flex flex-col gap-3">
          <div class="flex items-center justify-between">
            <h4 class="text-sm font-bold text-n-slate-12 flex items-center gap-2 m-0">
              <Icon icon="i-lucide-layers" class="size-4 text-n-brand-9" />
              Daftar Item SPK Cetak ({{ items.length }} Item)
            </h4>
            <Button
              color="blue"
              variant="ghost"
              size="sm"
              icon="i-lucide-plus"
              label="Tambah Item SPK"
              @click="addItem"
            />
          </div>

          <!-- Item Rows -->
          <div
            v-for="(item, idx) in items"
            :key="idx"
            class="p-4 bg-white dark:bg-n-solid-2 border border-n-weak rounded-xl space-y-3 relative shadow-sm"
          >
            <div class="flex items-center justify-between pb-2 border-b border-n-weak">
              <span class="text-xs font-bold text-n-brand-11">Item SPK #{{ idx + 1 }}</span>
              <button
                v-if="items.length > 1"
                @click="removeItem(idx)"
                class="text-n-red-9 hover:text-n-red-11 p-1 rounded hover:bg-n-red-2 transition-colors"
                title="Hapus Item"
              >
                <Icon icon="i-lucide-trash-2" class="size-4" />
              </button>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
              <!-- Stock ID -->
              <div class="flex flex-col gap-1">
                <label class="text-[11px] font-semibold text-n-slate-11">ID Stok / Produk (stock_id) *</label>
                <input
                  v-model="item.stock_id"
                  type="number"
                  placeholder="Contoh: 5"
                  class="w-full px-3 py-1.5 text-xs bg-n-surface-1 border border-n-weak rounded-lg focus:outline-none focus:ring-2 focus:ring-n-brand-6 text-n-slate-12 font-mono"
                />
              </div>

              <!-- Jumlah SPK -->
              <div class="flex flex-col gap-1">
                <label class="text-[11px] font-semibold text-n-slate-11">Jumlah (spk_jumlah) *</label>
                <input
                  v-model="item.spk_jumlah"
                  type="number"
                  min="1"
                  placeholder="1"
                  class="w-full px-3 py-1.5 text-xs bg-n-surface-1 border border-n-weak rounded-lg focus:outline-none focus:ring-2 focus:ring-n-brand-6 text-n-slate-12 font-medium"
                />
              </div>

              <!-- Diskon Rate (%) -->
              <div class="flex flex-col gap-1">
                <label class="text-[11px] font-semibold text-n-slate-11">Diskon (%)</label>
                <input
                  v-model="item.disc_rate"
                  type="number"
                  placeholder="0"
                  class="w-full px-3 py-1.5 text-xs bg-n-surface-1 border border-n-weak rounded-lg focus:outline-none focus:ring-2 focus:ring-n-brand-6 text-n-slate-12"
                />
              </div>

              <!-- Ukuran -->
              <div class="flex flex-col gap-1">
                <label class="text-[11px] font-semibold text-n-slate-11">Ukuran SPK</label>
                <input
                  v-model="item.spk_ukuran"
                  type="text"
                  placeholder="A3 / 30x40cm"
                  class="w-full px-3 py-1.5 text-xs bg-n-surface-1 border border-n-weak rounded-lg focus:outline-none focus:ring-2 focus:ring-n-brand-6 text-n-slate-12"
                />
              </div>

              <!-- Warna -->
              <div class="flex flex-col gap-1">
                <label class="text-[11px] font-semibold text-n-slate-11">Warna SPK</label>
                <input
                  v-model="item.spk_warna"
                  type="text"
                  placeholder="Full Color / Monochrome"
                  class="w-full px-3 py-1.5 text-xs bg-n-surface-1 border border-n-weak rounded-lg focus:outline-none focus:ring-2 focus:ring-n-brand-6 text-n-slate-12"
                />
              </div>

              <!-- Finishing -->
              <div class="flex flex-col gap-1">
                <label class="text-[11px] font-semibold text-n-slate-11">Finishing SPK</label>
                <input
                  v-model="item.spk_finishing"
                  type="text"
                  placeholder="Laminasi Glossy / Doff"
                  class="w-full px-3 py-1.5 text-xs bg-n-surface-1 border border-n-weak rounded-lg focus:outline-none focus:ring-2 focus:ring-n-brand-6 text-n-slate-12"
                />
              </div>
            </div>

            <!-- Item Keterangan -->
            <div class="flex flex-col gap-1">
              <label class="text-[11px] font-semibold text-n-slate-11">Keterangan Item SPK</label>
              <input
                v-model="item.spk_keterangan"
                type="text"
                placeholder="Catatan pengerjaan item..."
                class="w-full px-3 py-1.5 text-xs bg-n-surface-1 border border-n-weak rounded-lg focus:outline-none focus:ring-2 focus:ring-n-brand-6 text-n-slate-12"
              />
            </div>
          </div>
        </div>

      </div>

      <!-- Modal Footer -->
      <div class="px-6 py-4 border-t border-n-weak bg-n-surface-1 flex items-center justify-end gap-3 shrink-0">
        <Button color="slate" variant="ghost" @click="emit('close')">Batal</Button>
        <Button
          color="blue"
          icon="i-lucide-check"
          label="Simpan & Send Order ke ERP"
          :isLoading="isSubmitting"
          @click="handleSubmit"
        />
      </div>

    </div>
  </div>
</template>
